import * as React from "react";
import { ChevronDownIcon } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useEffect, useState } from "react";

interface DateAndTimePickerProps {
  startDateTime?: Date | null;
  endDateTime?: Date | null;
  onChange?: (dateStart: Date | null, dateEnd: Date | null) => void;
  minDate?: Date | null; // Minimum allowed date/time
  label?: string; // Custom label for the picker
}

export function DateAndTimePicker({
  startDateTime,
  endDateTime,
  onChange,
  minDate,
  label,
}: DateAndTimePickerProps) {
  const [open, setOpen] = useState(false);
  const [selectedDateStart, setSelectedDateStart] = useState<Date | null>(
    startDateTime || null
  );
  const [selectedDateEnd, setSelectedDateEnd] = useState<Date | null>(
    endDateTime || null
  );
  const [startTime, setStartTime] = useState<string>("");
  const [endTime, setEndTime] = useState<string>("");

  // Initialize time value when date changes
  useEffect(() => {
    if (startDateTime) {
      setSelectedDateStart(startDateTime);
      // Format time as HH:MM (no seconds)
      const hours = startDateTime.getHours().toString().padStart(2, "0");
      const minutes = startDateTime.getMinutes().toString().padStart(2, "0");
      setStartTime(`${hours}:${minutes}`);
    }
    if (endDateTime) {
      setSelectedDateEnd(endDateTime);
      // Format time as HH:MM (no seconds)
      const hours = endDateTime.getHours().toString().padStart(2, "0");
      const minutes = endDateTime.getMinutes().toString().padStart(2, "0");
      setEndTime(`${hours}:${minutes}`);
    }
  }, []);

  const handleDateChange = (date: Date) => {
    const newDateStart = new Date(date.toLocaleDateString() + " " + startTime);
    console.log("newDateStart", newDateStart);
    const newDateEnd = new Date(date.toLocaleDateString() + " " + endTime);
    console.log("newDateEnd", newDateEnd);
    setSelectedDateStart(newDateStart);
    setSelectedDateEnd(newDateEnd);

    setOpen(false);
    onChange?.(newDateStart, newDateEnd);
  };

  const handleTimeChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    isStartTime: boolean
  ) => {
    const time = e.target.value;
    if (isStartTime) {
      setStartTime(time);
      if (selectedDateStart) {
        const newDateStart = new Date(
          selectedDateStart.toLocaleDateString() + " " + time
        );
        setSelectedDateStart(newDateStart);
        onChange?.(newDateStart, selectedDateEnd);
      }
    } else {
      setEndTime(time);
      if (selectedDateEnd) {
        const newDateEnd = new Date(
          selectedDateEnd.toLocaleDateString() + " " + time
        );
        setSelectedDateEnd(newDateEnd);
        onChange?.(selectedDateStart, newDateEnd);
      }
    }
  };

  return (
    <div className="flex gap-4">
      <div className="flex flex-col gap-3">
        <Label htmlFor="date-picker" className="px-1">
          {label || "Date"}
        </Label>
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              id="date-picker"
              className="w-32 justify-between font-normal"
            >
              {selectedDateStart
                ? selectedDateStart.toLocaleDateString()
                : "Select date"}
              <ChevronDownIcon />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto overflow-hidden p-0" align="start">
            <Calendar
              mode="single"
              required={true}
              selected={selectedDateStart ?? undefined}
              captionLayout="dropdown"
              onSelect={handleDateChange}
            />
          </PopoverContent>
        </Popover>
      </div>
      <div className="flex flex-col gap-3">
        <Label htmlFor="time-picker" className="px-1">
          Time
        </Label>
        <Input
          type="time"
          id="time-picker"
          value={startTime}
          onChange={(e) => handleTimeChange(e, true)}
          className="bg-background appearance-none [&::-webkit-calendar-picker-indicator]:hidden [&::-webkit-calendar-picker-indicator]:appearance-none"
        />
      </div>
      <div className="flex items-end gap-2">
        <span>To</span>
      </div>
      <div className="flex flex-col gap-3">
        <Label htmlFor="time-picker" className="px-1">
          Time
        </Label>
        <Input
          type="time"
          id="time-picker"
          value={endTime}
          onChange={(e) => handleTimeChange(e, false)}
          className="bg-background appearance-none [&::-webkit-calendar-picker-indicator]:hidden [&::-webkit-calendar-picker-indicator]:appearance-none"
        />
      </div>
    </div>
  );
}
